<template>
  <v-flex xs12 sm10 md4>
    <!-- Step 1: Login Form -->
    <v-card v-if="isStep(1)" class="elevation-12">
      <v-card-title class="primary align-self-center">
        <h2 class="headline ml-2 white--text">User Login</h2>
      </v-card-title>
      <v-card-text class="pa-10">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-text-field
            prepend-inner-icon="mdi-email"
            name="email"
            label="Email"
            type="email"
            v-model="email"
            :rules="emailRules"
            autocomplete="email"
            hint="Enter your registered email address"
            autofocus
            dense
            outlined
          ></v-text-field>
          <v-text-field
            prepend-inner-icon="mdi-account-key"
            label="Password"
            :type="showpwd ? 'text' : 'password'"
            v-model="password"
            :rules="passwordRules"
            autocomplete="current-password"
            dense
            outlined
            :append-icon="showpwd ? 'mdi-eye' : 'mdi-eye-off'"
            @click:append="showpwd = !showpwd"
            @keyup.enter.native="login"
          ></v-text-field>
          <div class="text-center">
            <div>
              Do not have an account?&nbsp;
              <router-link to="/register">Register</router-link>
            </div>
            <div>
              <router-link to="/resetpassword">Forgot Password?</router-link>
            </div>
          </div>
        </v-form>
      </v-card-text>
      <div class="text-center pb-10">
        <v-btn color="primary white--text" class="mr-2" to="/">Cancel</v-btn>
        <v-btn
          color="primary white--text"
          :loading="loading"
          :disabled="loading"
          @click="login"
          >Sign In</v-btn
        >
      </div>
    </v-card>

    <!-- Step 2: Email Verification Code -->
    <v-card v-if="isStep(2)" class="elevation-12">
      <v-card-title class="primary justify-center">
        <h2 class="headline white--text">Enter Verification Code</h2>
      </v-card-title>
      <v-card-text class="pa-10 text-center">
        <p class="mb-4">
          A 6-digit code has been sent to
          <strong>{{ email }}</strong
          >.
        </p>
        <v-text-field
          v-model="code"
          label="Enter 6-digit code"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          hint="The code expires 5 minutes after it is sent."
          persistent-hint
          dense
          outlined
          maxlength="6"
          @keyup.enter.native="verifyCode"
        ></v-text-field>
        <v-btn
          color="primary white--text"
          :loading="loading"
          :disabled="code.length !== 6"
          @click="verifyCode"
          >Verify &amp; Sign In</v-btn
        >
        <v-btn text class="mt-2" @click="cancelVerification" :disabled="loading"
          >Back</v-btn
        >
        <div class="mt-4">
          <a href="#" v-if="resendCooldown === 0" @click.prevent="sendCode"
            >Resend code</a
          >
          <span v-else class="caption"
            >Resend available in {{ resendCooldown }}s</span
          >
        </div>
      </v-card-text>
    </v-card>
  </v-flex>
</template>

<script>
import AuthLayout from "@/layouts/AuthLayout.vue";
import api from "../services/axiosconfig";
import { auth, db } from "../services/firebase";
import { doc, getDoc } from "firebase/firestore";
import {
  signInWithEmailAndPassword,
  signOut,
  setPersistence,
  browserSessionPersistence,
  sendEmailVerification,
} from "@firebase/auth";

export default {
  name: "login",
  data() {
    return {
      flowStep: 1,
      loading: false,
      valid: false,
      password: "",
      showpwd: false,
      code: "",
      ledgerkey: "",
      username: "",
      address1: "",
      address2: "",
      address3: "",
      pincode: "",
      restriction: {},
      userCredentials: null,
      resendCooldown: 0,
      cooldownTimer: null,
      passwordRules: [
        (v) => !!v || "Password is required",
        (v) =>
          (v && v.length >= 6) || "Password must be less than 6 characters",
      ],
      email: "",
      emailRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],
    };
  },

  created() {
    this.$emit(`update:layout`, AuthLayout);
  },

  beforeDestroy() {
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
  },

  methods: {
    isStep(step) {
      return this.flowStep === step;
    },

    startCooldown(seconds) {
      this.resendCooldown = seconds;
      if (this.cooldownTimer) clearInterval(this.cooldownTimer);
      this.cooldownTimer = setInterval(() => {
        if (this.resendCooldown > 0) {
          this.resendCooldown--;
        } else {
          clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
        }
      }, 1000);
    },

    async login() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        try {
          await setPersistence(auth, browserSessionPersistence);
          const userCredentials = await signInWithEmailAndPassword(
            auth,
            this.email,
            this.password
          );
          this.userCredentials = userCredentials;
          if (!userCredentials.user.emailVerified) {
            await sendEmailVerification(auth.currentUser);
            this.$toasted.global.optimus_info({
              message:
                "A verification email link has been sent to your registered email address.",
            });
            signOut(auth);
            this.clearState();
            return;
          }
          // The password is deliberately left in the model here. Clearing it
          // re-runs the field's validation against an empty value, which flips
          // the form's `valid` and disables the Sign In button. It is scrubbed
          // in cancelVerification() and on the verifyCode() success path.
          await this.sendCode();
          this.loading = false;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log("Login error:", error);
          signOut(auth);
          this.clearState();
          this.loading = false;
          if (error.code) {
            this.$toasted.global.optimus_error({
              message: error.message || `Firebase Auth error: ${error.code}`,
            });
          } else {
            this.$toasted.global.optimus_error({
              message: `Could not sign in to your account. ${
                error.message || "Unknown error"
              }`,
            });
          }
        }
      }
    },

    async sendCode() {
      this.loading = true;
      try {
        const idToken = await this.userCredentials.user.getIdToken();
        await api.post(
          "/aejmis/api/2fa/send",
          {},
          {
            headers: { Authorization: `Bearer ${idToken}` },
          }
        );
        this.$toasted.global.optimus_info({
          message: "A verification code has been sent to your email.",
        });
        this.flowStep = 2;
        this.startCooldown(60);
        this.loading = false;
      } catch (error) {
        this.loading = false;
        if (
          error.response &&
          error.response.data &&
          error.response.data.code === "COOLDOWN_ACTIVE"
        ) {
          this.$toasted.global.optimus_error({
            message: error.response.data.error,
          });
          const match = error.response.data.error.match(/(\d+)/);
          if (match) this.startCooldown(parseInt(match[1]));
        } else {
          let msg = "Could not send verification code.";
          if (error.response) {
            const serverMsg = error.response.data && error.response.data.error;
            msg += serverMsg
              ? ` ${serverMsg}`
              : ` (HTTP ${error.response.status})`;
          } else if (error.message) {
            msg += ` (${error.message})`;
          }
          // eslint-disable-next-line no-console
          console.error("sendCode error:", error);
          this.$toasted.global.optimus_error({ message: msg });
        }
      }
    },

    cancelVerification() {
      signOut(auth);
      this.password = "";
      this.clearState();
      this.$toasted.global.optimus_info({
        message: "Sign-in cancelled. Please sign in again.",
      });
    },

    async verifyCode() {
      const digits = String(this.code).replace(/\D/g, "").slice(0, 6);
      this.code = digits;
      if (digits.length !== 6) return;
      this.loading = true;
      try {
        const idToken = await this.userCredentials.user.getIdToken();
        const response = await api.post(
          "/aejmis/api/2fa/verify",
          { code: this.code },
          {
            headers: { Authorization: `Bearer ${idToken}` },
          }
        );
        if (response.data.valid) {
          // The server stamps the 2FA result onto the ID token as a custom
          // claim, and every data endpoint requires it. Force a refresh so the
          // token used for the rest of the session carries the claim.
          await this.userCredentials.user.getIdToken(true);

          const snapshot = await getDoc(
            doc(db, "users", this.userCredentials.user.uid)
          );
          if (snapshot.exists()) {
            this.ledgerkey = snapshot.data().ledgerkey;
            this.username = snapshot.data().name;
            this.address1 = snapshot.data().address1;
            this.address2 = snapshot.data().address2;
            this.address3 = snapshot.data().address3;
            this.pincode = snapshot.data().pincode;
            this.restriction = snapshot.data().restriction || {};
          }
          localStorage.setItem("LEDG_KEY", this.ledgerkey);
          localStorage.setItem("USER_NAME", this.username);
          localStorage.setItem("ADDRESS_1", this.address1);
          localStorage.setItem("ADDRESS_2", this.address2);
          localStorage.setItem("ADDRESS_3", this.address3);
          localStorage.setItem("PIN_CODE", this.pincode);
          localStorage.setItem("PERMITTED", this.restriction.value);
          this.$store.dispatch("setUser", this.userCredentials.user);
          // The step 1 form is already unmounted at this point, so clearing
          // the password here cannot re-trigger its validation.
          this.password = "";
          this.$router.replace("/dashboard");
          this.loading = false;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        this.loading = false;
        this.code = "";
        if (error.response && error.response.data) {
          this.$toasted.global.optimus_error({
            message: error.response.data.error || "Could not verify code.",
          });
        } else {
          this.$toasted.global.optimus_error({
            message: "Could not verify code. Something went wrong.",
          });
        }
      }
    },

    clearState() {
      this.flowStep = 1;
      this.loading = false;
      this.code = "";
      this.userCredentials = null;
      this.$store.dispatch("setUser", null);
      if (this.cooldownTimer) clearInterval(this.cooldownTimer);
      this.resendCooldown = 0;
      this.$router.push("/");
    },
  },
};
</script>
